import { Point, AnimatedSprite, Container, Texture, Rectangle } from "pixi.js";
import { randomInRange } from "../utils/math";
import { StickTextures } from "./Textures";
import { createStick } from "./Stick";
import { createInstructions } from "./Instructions";

export type XDirection = "left" | "right";
export type YDirection = "up" | "down";
export type Direction = XDirection | YDirection;

const MAX_SPAWN_BLINKS = 20;
const FRAMES_PER_SPAWN_BLINK = 10;

type FavIconTexture = {
  texture: Texture;
  direction: XDirection;
};

type Player = {
  update: (fireRect: Rectangle) => void;
  resize: () => void;
  walk: (direction: Direction) => void;
  getFavIconTexture: () => FavIconTexture;
  container: Container;
};

type PlayerParams = {
  isNPC: boolean;
  screensaver: boolean;
  stickTextures: StickTextures;
  textures: Texture[];
};

const MAX_SPEED = 6;
const ACCEL_AMOUNT = 4;
const DRAG = 0.95;
const FORBIDDEN_ZONE = 100;

export function createPlayer({
  textures,
  stickTextures,
  screensaver,
  isNPC = false,
}: PlayerParams): Player {
  let framesSinceSpawn = 0;
  let blinksSinceSpawn = 0;
  let acceleration = new Point();
  let velocity = new Point();
  let hasWalked = false;
  let lastXDirection: XDirection = randomInRange(0, 1) === 1 ? "left" : "right";
  const stick = createStick(stickTextures);

  const container = new Container();
  const gfxContainer = new Container();
  const instructionsContainer = new Container();
  container.addChild(gfxContainer);
  container.addChild(instructionsContainer);

  const sprite = new AnimatedSprite(textures);
  sprite.animationSpeed = 0.15;
  sprite.position.x = -32;
  sprite.position.y = -32;

  gfxContainer.addChild(sprite);
  if (isNPC) {
    gfxContainer.visible = false;
  }

  if (!isNPC || (!screensaver && randomInRange(0, 3) === 2)) {
    gfxContainer.addChild(stick.container);
  }

  // instructions
  if (!isNPC) {
    const instructions = createInstructions();
    instructionsContainer.addChild(instructions.container);
  }

  const hitTestForbiddenZone = (p: Point) => {
    if (screensaver) {
      return false;
    }

    const dw = document.documentElement.clientWidth;
    const dh = document.documentElement.clientHeight;

    const centerX = dw / 2;
    const centerY = dh / 2;
    const left = centerX - FORBIDDEN_ZONE;
    const right = centerX + FORBIDDEN_ZONE;
    const top = centerY - FORBIDDEN_ZONE;
    const bottom = centerY + FORBIDDEN_ZONE;
    return p.x > left && p.x < right && p.y > top && p.y < bottom;
  };

  const walk = (direction: Direction) => {
    hasWalked = true;

    if (direction === "left") {
      acceleration.x -= ACCEL_AMOUNT;
      lastXDirection = direction;
    }
    if (direction === "right") {
      acceleration.x += ACCEL_AMOUNT;
      lastXDirection = direction;
    }
    if (direction === "up") {
      acceleration.y -= ACCEL_AMOUNT;
    }
    if (direction === "down") {
      acceleration.y += ACCEL_AMOUNT;
    }
  };

  const checkMarshmellow = (fireRect: Rectangle) => {
    if (screensaver) {
      return;
    }

    if (!stick.container.parent) {
      return;
    }

    if (fireRect.width === 0 || fireRect.height === 0) {
      return;
    }

    const stickRect = stick.getHitArea();

    if (stickRect.width === 0 || stickRect.height === 0) {
      return;
    }

    const isMelting = fireRect.intersects(stickRect);
    stick.setIsMelting(isMelting);
  };

  const isSpawnFinished = () => {
    return isNPC || blinksSinceSpawn >= MAX_SPAWN_BLINKS;
  };

  const updateSpawnBlink = () => {
    if (!isNPC && !isSpawnFinished()) {
      framesSinceSpawn++;
      if (framesSinceSpawn >= FRAMES_PER_SPAWN_BLINK) {
        framesSinceSpawn = 0;
        gfxContainer.visible = !gfxContainer.visible;
        blinksSinceSpawn++;
      }
    } else {
      gfxContainer.visible = true;
    }
  };

  const updateInstructionArrows = () => {
    if (
      !isNPC &&
      hasWalked &&
      instructionsContainer.parent &&
      isSpawnFinished()
    ) {
      instructionsContainer.removeFromParent();
    }
  };

  const updatePosition = () => {
    velocity.x += acceleration.x;
    velocity.y += acceleration.y;

    if (acceleration.x > 0) {
      acceleration.x -= ACCEL_AMOUNT;
    } else if (acceleration.x < 0) {
      acceleration.x += ACCEL_AMOUNT;
    }

    if (acceleration.y > 0) {
      acceleration.y -= ACCEL_AMOUNT;
    } else if (acceleration.y < 0) {
      acceleration.y += ACCEL_AMOUNT;
    }

    if (velocity.x > MAX_SPEED) {
      velocity.x = MAX_SPEED;
    }
    if (velocity.x < -MAX_SPEED) {
      velocity.x = -MAX_SPEED;
    }
    if (velocity.y > MAX_SPEED) {
      velocity.y = MAX_SPEED;
    }
    if (velocity.y < -MAX_SPEED) {
      velocity.y = -MAX_SPEED;
    }

    velocity.x *= DRAG;
    velocity.y *= DRAG;
    if (Math.abs(velocity.x) < 0.01) velocity.x = 0;
    if (Math.abs(velocity.y) < 0.01) velocity.y = 0;

    const lastPosition = container.position.clone();
    const newPosition = new Point(
      lastPosition.x + velocity.x,
      lastPosition.y + velocity.y
    );
    if (hitTestForbiddenZone(newPosition)) {
      container.position = lastPosition;
      velocity = new Point();
    } else {
      container.position = newPosition;
    }

    const dw = document.documentElement.clientWidth;
    const dh = document.documentElement.clientHeight;

    if (container.position.x > dw + 32) {
      container.position.x = 0;
    }
    if (container.position.x < -32) {
      container.position.x = dw + 32;
    }

    if (container.position.y > dh + 32) {
      container.position.y = 0;
    }
    if (container.position.y < -32) {
      container.position.y = dh + 32;
    }

    container.scale.x = lastXDirection === "left" ? 1 : -1;
    container.zIndex = !isSpawnFinished() ? 9999999 : container.position.y;
    const samePosition = lastPosition.equals(container.position);

    if (samePosition && sprite?.playing) {
      sprite?.gotoAndStop(0);
      container.scale.x = lastXDirection === "left" ? 1 : -1;
    } else if (!samePosition && !sprite?.playing) {
      sprite?.play();
    }
  };

  const getFavIconTexture = (): FavIconTexture => {
    const texture = sprite.texture;

    return {
      texture,
      direction: lastXDirection,
    };
  };

  const update = (fireRect: Rectangle) => {
    updateSpawnBlink();
    updateInstructionArrows();
    updatePosition();
    if (!screensaver) {
      checkMarshmellow(fireRect);
      stick.update();
    }
  };

  if (isNPC) {
    let int: NodeJS.Timeout | number = -1;
    const sleep = () => {
      clearInterval(int);
      int = setTimeout(wake, randomInRange(4000, 8000));
    };

    const wake = () => {
      const diceRoll = randomInRange(0, 12) === 2;
      if (!diceRoll) {
        sleep();
        return;
      }

      // have NPCS occassionally biased toward the fire
      const towardFire = randomInRange(0, 7) === 2;
      if (towardFire) {
        const dw = document.documentElement.clientWidth;
        const dh = document.documentElement.clientHeight;

        const centerX = dw / 2;
        const centerY = dh / 2;

        const dx = centerX - container.position.x;
        const dy = centerY - container.position.y;

        if (Math.abs(dx) > Math.abs(dy)) {
          walk(dx > 0 ? "right" : "left");
        } else {
          walk(dy > 0 ? "down" : "up");
        }
      } else {
        new Array(randomInRange(2, 5)).fill(1).map(() => {
          const diceRoll = randomInRange(0, 4);
          if (diceRoll === 0) walk("left");
          if (diceRoll === 1) walk("right");
          if (diceRoll === 2) walk("up");
          if (diceRoll === 3) walk("down");
        });
      }

      sleep();
    };

    const diceRoll = randomInRange(0, 12) === 2;
    diceRoll ? wake() : sleep();
  }

  const resize = () => {
    const dw = document.documentElement.clientWidth;
    const dh = document.documentElement.clientHeight;

    let startPos = new Point(
      randomInRange(0, dw - 32),
      randomInRange(0, dh - 32)
    );
    while (hitTestForbiddenZone(startPos)) {
      startPos = new Point(
        randomInRange(0, dw - 32),
        randomInRange(0, dh - 32)
      );
    }
    container.position = startPos.clone();
  };

  resize();

  return {
    update,
    resize,
    walk,
    getFavIconTexture,
    container,
  };
}
